import { useState } from "react"
import { useNavigate } from "@tanstack/react-router"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"
import { WebappRoutes } from "@/shared/lib/webapp-routes"

import { ReportIds } from "../../config/reports-data"

import HeartHero from "@/assets/img/today/reports/love-match-ready-hero.webp"

/** Close animation duration before the popover unmounts (matches CSS). */
const CLOSE_MS = 220

/**
 * "Your Love Match Report Is Ready to Reveal" popover — shown when the user taps
 * the Love Match Report after its unlock timer has finished. A centered card with
 * the crystal-heart hero (You / Partner), a lead-in, and an "Open Love Match
 * report" CTA that navigates to the report view. Ported 1:1 from Figma
 * "Love_Match_Report_Ready_Popover". Dismiss via the X or the backdrop.
 */
export function LoveMatchReadyPopover({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate()
  const [closing, setClosing] = useState(false)

  // Play the fade/scale-out, then unmount — mirrors the open.
  const close = () => {
    if (closing) return
    setClosing(true)
    window.setTimeout(onClose, CLOSE_MS)
  }

  const open = () => {
    navigate({ to: WebappRoutes.REPORT_VIEW, search: { report: ReportIds.LoveMatch } })
    onClose()
  }

  return (
    <div className="fixed inset-0 z-[1150] mx-auto flex w-full max-w-app items-center justify-center px-4">
      <button
        type="button"
        aria-label="Close"
        onClick={close}
        className={cn(
          "absolute inset-0 h-full w-full bg-dark-base/80 backdrop-blur-sm",
          closing ? "astro-sheet-fade-out" : "astro-sheet-fade",
        )}
      />

      <div
        data-slot="love-match-ready-popover"
        className={cn(
          "relative w-full overflow-hidden rounded-[28px] bg-dark-base pt-4 shadow-[0_0_40px_rgba(100,30,220,0.35)]",
          closing ? "astro-pop-out" : "astro-pop-in",
        )}
      >
        {/* Top purple glow. */}
        <div className="pointer-events-none absolute inset-x-0 top-0 h-32 bg-[radial-gradient(120%_80%_at_50%_0%,rgba(100,30,220,0.4),transparent_70%)]" />

        <button
          type="button"
          aria-label="Close"
          onClick={close}
          className="absolute right-4 top-4 z-10 flex size-6 items-center justify-center rounded-full bg-white/10 text-white/70 [&_svg]:size-4"
        >
          <X />
        </button>

        <img src={HeartHero} alt="Love Match crystal heart" className="w-full" />

        <div className="relative flex flex-col items-center px-4 pb-4 pt-4">
          <h2 className="text-center font-display text-[26px] font-medium leading-tight text-purple-light">
            Your Love Match Report
            <br />
            Is Ready to Reveal
          </h2>
          <p className="mt-2 text-center font-body text-[15px] leading-snug text-purple-light/70">
            Discover your compatibility, emotional dynamics, and the deeper patterns shaping this
            connection
          </p>

          <button
            type="button"
            onClick={open}
            className="mt-4 h-14 w-full rounded-full bg-gradient-to-b from-peach to-purple-accent font-body text-base font-semibold text-white"
          >
            Open Love Match report
          </button>
        </div>
      </div>
    </div>
  )
}
